import { useState, useEffect } from 'react'
import { useAuth } from '@talktoast/shared'
import {
  PersistentPreferences,
  DietaryPreference,
  DIETARY_PREFERENCES,
  UNITS_SYSTEM_PREFERENCES,
  MEASUREMENT_BIAS_PREFERENCES,
  AVAILABLE_APPLIANCES,
  COMMON_RESTRICTIONS,
  DEFAULT_PERSISTENT_PREFERENCES
} from '@talktoast/shared'
import { Button, Input, Card, Badge } from '@talktoast/ui'

interface InitialSetupProps {
  onComplete: (preferences: PersistentPreferences) => void
  startStep?: 1 | 2 | 3 | 4  // Allow jumping to specific step
  initialPreferences?: PersistentPreferences  // Pre-populate with existing preferences
  fromSettings?: boolean  // Track if accessed from settings for different button text
  onBack?: () => void  // Callback for settings "Save & Back" button
  singleStepMode?: boolean  // If true, only show the startStep
}

export default function InitialSetup({
  onComplete,
  startStep = 1,
  initialPreferences,
  fromSettings = false,
  onBack,
  singleStepMode = false
}: InitialSetupProps) {
  const { user } = useAuth()
  const [step, setStep] = useState(startStep)
  const [preferences, setPreferences] = useState<PersistentPreferences>(
    initialPreferences || DEFAULT_PERSISTENT_PREFERENCES
  )
  const [customRestriction, setCustomRestriction] = useState('')
  const [error, setError] = useState('')

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const totalSteps = singleStepMode ? 1 : 4

  const addRestriction = (restriction: string) => {
    if (restriction && !preferences.dietaryRestrictions.includes(restriction)) {
      setPreferences(prev => ({
        ...prev,
        dietaryRestrictions: [...prev.dietaryRestrictions, restriction]
      }))
    }
    setCustomRestriction('')
  }

  const removeRestriction = (restriction: string) => {
    setPreferences(prev => ({
      ...prev,
      dietaryRestrictions: prev.dietaryRestrictions.filter(r => r !== restriction)
    }))
  }

  const toggleAppliance = (appliance: string) => {
    setPreferences(prev => ({
      ...prev,
      kitchenAppliances: prev.kitchenAppliances.includes(appliance)
        ? prev.kitchenAppliances.filter(a => a !== appliance)
        : [...prev.kitchenAppliances, appliance]
    }))
  }

  const handleSave = () => {
    // Save and go back to settings
    onComplete(preferences)
    if (fromSettings && onBack) {
      onBack()
    }
  }

  const nextStep = () => {
    // In single step mode or from settings, save immediately
    if (singleStepMode || (fromSettings && step === startStep)) {
      handleSave()
      return
    }

    if (step < totalSteps) {
      setStep(step + 1)
    } else {
      // Validate and complete setup
      if (!preferences.primaryDietaryPreference) {
        setError('Please select a dietary preference')
        return
      }
      if (!preferences.unitsSystem) {
        setError('Please select a units system')
        return
      }
      if (!preferences.measurementBias) {
        setError('Please select a measurement preference')
        return
      }
      if (preferences.kitchenAppliances.length === 0) {
        setError('Please select at least one appliance')
        return
      }
      onComplete(preferences)
    }
  }

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1)
      setError('')
    }
  }

  const canProceed = () => {
    switch (step) {
      case 1:
        return preferences.primaryDietaryPreference !== null
      case 2:
        return true // Dietary restrictions are optional
      case 3:
        return preferences.unitsSystem !== null && preferences.measurementBias !== null
      case 4:
        return preferences.kitchenAppliances.length > 0
      default:
        return false
    }
  }

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Step {step} of {totalSteps}</span>
        <span>{Math.round((step / totalSteps) * 100)}% complete</span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>

      {error && (
        <Card variant="outlined" className="bg-red-50 border-red-200">
          <p className="text-red-700 text-sm">{error}</p>
        </Card>
      )}

      {/* Step Content */}
      <div className="space-y-6">
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">What's your dietary preference?</h2>
              <p className="text-gray-600 mt-2">This helps us suggest the right recipes for you</p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {DIETARY_PREFERENCES.map(diet => (
                <Card
                  key={diet.value}
                  variant={preferences.primaryDietaryPreference === diet.value ? 'elevated' : 'outlined'}
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    primaryDietaryPreference: diet.value
                  }))}
                  className={`cursor-pointer transition-colors ${
                    preferences.primaryDietaryPreference === diet.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium">{diet.label}</div>
                  <div className="text-sm text-gray-600">{diet.description}</div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Any dietary restrictions?</h2>
              <p className="text-gray-600 mt-2">Help us avoid ingredients you can't or don't want to eat</p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {COMMON_RESTRICTIONS.map(restriction => (
                  <Button
                    key={restriction}
                    variant={preferences.dietaryRestrictions.includes(restriction) ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() =>
                      preferences.dietaryRestrictions.includes(restriction)
                        ? removeRestriction(restriction)
                        : addRestriction(restriction)
                    }
                    className={`rounded-full ${
                      preferences.dietaryRestrictions.includes(restriction)
                        ? 'bg-orange-500 border-orange-500 hover:bg-orange-600'
                        : ''
                    }`}
                  >
                    {restriction}
                  </Button>
                ))}
              </div>

              <div className="flex gap-2">
                <Input
                  type="text"
                  value={customRestriction}
                  onChange={(e) => setCustomRestriction(e.target.value)}
                  placeholder="Add custom restriction"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addRestriction(customRestriction)
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() => addRestriction(customRestriction)}
                  disabled={!customRestriction.trim()}
                >
                  Add
                </Button>
              </div>

              {preferences.dietaryRestrictions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Your restrictions:</p>
                  <div className="flex flex-wrap gap-2">
                    {preferences.dietaryRestrictions.map(restriction => (
                      <Badge
                        key={restriction}
                        variant="warning"
                        className="inline-flex items-center gap-1 bg-orange-50 text-orange-700"
                      >
                        {restriction}
                        <button
                          type="button"
                          onClick={() => removeRestriction(restriction)}
                          className="text-orange-500 hover:text-orange-700 ml-1"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Measurement Preferences</h2>
              <p className="text-gray-600 mt-2">Help us show ingredients in units you're familiar with</p>
            </div>

            {/* Units System */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900">Which measurement system do you prefer?</h3>
              <div className="grid grid-cols-1 gap-3">
                {UNITS_SYSTEM_PREFERENCES.map(units => (
                  <Card
                    key={units.value}
                    variant={preferences.unitsSystem === units.value ? 'elevated' : 'outlined'}
                    onClick={() => setPreferences(prev => ({
                      ...prev,
                      unitsSystem: units.value
                    }))}
                    className={`cursor-pointer transition-colors ${
                      preferences.unitsSystem === units.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium">{units.label}</div>
                    <div className="text-sm text-gray-600">{units.description}</div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Measurement Bias */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900">How do you prefer to measure ingredients?</h3>
              <div className="grid grid-cols-1 gap-3">
                {MEASUREMENT_BIAS_PREFERENCES.map(bias => (
                  <Card
                    key={bias.value}
                    variant={preferences.measurementBias === bias.value ? 'elevated' : 'outlined'}
                    onClick={() => setPreferences(prev => ({
                      ...prev,
                      measurementBias: bias.value
                    }))}
                    className={`cursor-pointer transition-colors ${
                      preferences.measurementBias === bias.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium">{bias.label}</div>
                    <div className="text-sm text-gray-600">{bias.description}</div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">What's in your kitchen?</h2>
              <p className="text-gray-600 mt-2">Select the appliances you have available for cooking</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_APPLIANCES.map(appliance => (
                <Button
                  key={appliance.value}
                  variant={preferences.kitchenAppliances.includes(appliance.value) ? 'primary' : 'outline'}
                  size="md"
                  onClick={() => toggleAppliance(appliance.value)}
                  className={`${
                    preferences.kitchenAppliances.includes(appliance.value)
                      ? 'bg-green-500 border-green-500 hover:bg-green-600'
                      : ''
                  }`}
                >
                  <span className="mr-2">{appliance.emoji}</span>
                  {appliance.label}
                </Button>
              ))}
            </div>

            {preferences.kitchenAppliances.length > 0 && (
              <Card variant="outlined" className="bg-green-50 border-green-200">
                <p className="text-green-800 text-sm font-medium">
                  Selected: {preferences.kitchenAppliances.join(', ').replace(/_/g, ' ')}
                </p>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          size="lg"
          onClick={fromSettings && onBack ? onBack : prevStep}
          disabled={!fromSettings && step === 1}
        >
          {fromSettings ? 'Cancel' : 'Previous'}
        </Button>

        <Button
          variant="primary"
          size="lg"
          onClick={nextStep}
          disabled={!canProceed()}
        >
          {fromSettings || singleStepMode ? 'Save & Back' : (step === totalSteps ? 'Complete Setup' : 'Next')}
        </Button>
      </div>
    </div>
  )
}